import React, { useEffect, useRef, useState, useContext, useCallback } from 'react';
import api from '../../../services/api/axiosConfig';
import API_ENDPOINTS, { buildEndpoint } from '../../../services/api/endpoints';
import { Link,useParams } from 'react-router-dom';

import useEcho from "../../../hooks/echo";
import useEchoChannel from "../../../hooks/useEchoChannel";


export default function MessageList() {
    const { id } = useParams();
    const echo = useEcho();

    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const containerRef = useRef(null);
    const messageEndRef = useRef(null);
    const loggedInUser = JSON.parse(localStorage.getItem('user'));
    const loggedInUserId = loggedInUser?.id;
    const [customerInfo, setCustomerInfo] = useState(null);

    const [isSending, setIsSending] = useState(false);

    // Keep track of the initial load to ensure we scroll to the bottom initially
    const isInitialLoad = useRef(true);


    const fetchCustomerInfo = async () => {
        try {
            // Assuming your customer API endpoint follows a similar pattern
            // You might need to adjust this endpoint based on your actual API structure
            const endpoint = buildEndpoint(API_ENDPOINTS.PANEL.CUSTOMER.MESSAGE.GET_CUSTOMER_INFO, { id }); 
            const res = await api.get(endpoint);
            if (res.data.success && res.data.data.length > 0) {
                setCustomerInfo(res.data.data[0]);
            } else {
                console.warn('Customer info not found or API response invalid:', res.data);
            }
        } catch (err) {
            console.error('Failed to fetch customer info:', err);
        }
    };

    const fetchMessages = async (page = 1, append = false) => {
        const endpoint = buildEndpoint(API_ENDPOINTS.PANEL.CUSTOMER.MESSAGE.LISTING, { id });

        const container = containerRef.current;
        let previousScrollHeight = 0;
        if (append && container) {
            previousScrollHeight = container.scrollHeight;
        }

        try {
            const res = await api.get(endpoint, {
                params: { page },
            });

            const newMessages = res.data.data.reverse() || [];
            setPagination(res.data.pagination || {});

            setMessages((prev) => {
                const combinedMessages = append ? [...newMessages, ...prev] : [...newMessages];
                // Remove duplicates if any (important for real-time updates + fetching)
                const uniqueMessages = Array.from(new Set(combinedMessages.map(msg => msg.id)))
                                          .map(id => combinedMessages.find(msg => msg.id === id));
                return uniqueMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            });


            setTimeout(() => {
                if (append && container) {
                    // Adjust scroll position to keep the old messages in view
                    const newScrollHeight = container.scrollHeight;
                    container.scrollTop = newScrollHeight - previousScrollHeight;
                } else if (isInitialLoad.current) {
                    // Only scroll to bottom on initial load
                    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                    isInitialLoad.current = false; // Mark initial load as complete
                }
            }, 50);

        } catch (err) {
            console.error('Failed to fetch messages:', err);
        } finally {
            setLoading(false);
            setIsLoadingMore(false);
        }
    };

    const sendMessage = async () => {
        setIsSending(true);
        if (!newMessage.trim()) {
            console.log("Message is empty, aborting.");
            setIsSending(false);
            return;
        }
        try {
            await api.post(API_ENDPOINTS.PANEL.CUSTOMER.MESSAGE.SEND, {
                conversation_id: id,
                message_body: newMessage,
            });

            setNewMessage('');
            // After sending, we want to fetch messages to get the new one and scroll to bottom
            // Reset isInitialLoad to true to force scroll to bottom on next fetch
            isInitialLoad.current = true;
            fetchMessages();

        } catch (err) {
            console.error('Failed to send message:', err);
        } finally {
            setIsSending(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleScroll = () => {
        const container = containerRef.current;
        if (!container || isLoadingMore) return;

        if (container.scrollTop === 0 && pagination.current_page < pagination.last_page) {
            // No longer automatically load on scroll to top if you prefer the button
            // If you want both, keep this, but the button provides explicit control.
            // For now, let's assume the button is the primary way to load more.
            // setIsLoadingMore(true);
            // fetchMessages(pagination.current_page + 1, true);
        }
    };

    const handleNewMessageReceived = useCallback((event) => {
        console.log("Real-time message received via Echo:", event);
        // When a real-time message is received, ensure we scroll to the bottom
        isInitialLoad.current = true;
        fetchMessages();
    }, [loggedInUserId]);

    useEchoChannel(
        echo,
        loggedInUserId ? `user.${loggedInUserId}` : null,
        'MessageSendNotification',
        handleNewMessageReceived
    );

    useEffect(() => {
        document.title = "Chat with customer";
        // Initial fetch when component mounts or conversation changes
        isInitialLoad.current = true; // Reset for new conversations
        fetchMessages();
        fetchCustomerInfo();

    }, [id]);

    // This useEffect is now primarily for initial load and new messages coming in
    // It will be triggered when `isInitialLoad.current` is true, which is set
    // when sending a new message or receiving a real-time message.
    useEffect(() => {
        if (isInitialLoad.current && !isLoadingMore) {
            messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            // Don't set isInitialLoad.current to false here,
            // as it's handled within fetchMessages's setTimeout.
        }
    }, [messages.length, isLoadingMore]);


    const handleShowPrevious = () => {
        if (pagination.current_page < pagination.last_page && !isLoadingMore) {
            setIsLoadingMore(true);
            fetchMessages(pagination.current_page + 1, true);
        }
    };

    return (
        <div className="flex flex-col h-full border rounded shadow-md">
           {/* --- Customer Info Display --- */}
            <div className="p-4 border-b bg-white flex items-center justify-between">
                <h2 className="text-lg font-semibold">

                    {customerInfo ?
                    
                    <Link to={`/dashboard/customer/${customerInfo.id}`} className="hover:underline">

                    {customerInfo.firstname} {customerInfo.lastname}
                    </Link>

                    
                    : 'Loading Customer...'}
                </h2>
            </div>
            {/* --- End Customer Info Display --- */}
            <div
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50"
            >
                {/* Show Previous Button */}
                {pagination.current_page < pagination.last_page && (
                    <div className="text-center mb-4">
                        <button
                            onClick={handleShowPrevious}
                            disabled={isLoadingMore}
                            className={`px-4 py-2 rounded text-sm ${isLoadingMore ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                        >
                            {isLoadingMore ? 'Loading...' : 'Show Previous Messages'}
                        </button>
                    </div>
                )}

                {isLoadingMore && pagination.current_page < pagination.last_page && (
                    <div className="text-center text-sm text-gray-500">Loading older messages...</div>
                )}
                {messages.map((msg) => (
                    <div
                        key={msg.id}
                        className={`p-3 rounded max-w-xs ${msg.sender_id == loggedInUserId ? 'bg-blue-500 text-white ml-auto' : 'bg-gray-300 text-black'
                            }`}
                    >
                        <div className="text-sm whitespace-pre-line">{msg.body}</div>
                        <div className="text-[10px] text-right mt-1 opacity-70">
                            {new Date(msg.created_at).toLocaleTimeString()}
                        </div>
                    </div>
                ))}
                <div ref={messageEndRef} />
            </div>

            <div className="p-3 border-t bg-white">
                <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyPress}
                    rows={2}
                    className="w-full p-2 border rounded resize-none focus:outline-none focus:ring"
                    placeholder="Type your message and press Enter..."
                />
                <button
                    onClick={sendMessage}
                    disabled={isSending || !newMessage.trim()}
                    className={`mt-2 px-4 py-1 text-white rounded ${isSending || !newMessage.trim() ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                >
                    {isSending ? 'Sending...' : 'Send'}
                </button>
            </div>
        </div>
    );
}